import React, { useState, useEffect } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';
import { useSelector } from "react-redux";

const SchemeDocumentRatioDashboard = () => {
  const pid = useSelector((state) => state.user.projectId);
  const [data, setData] = useState({ scheme_count: 0, document_count: 0 });
  const [states, setStates] = useState([]);
  const [selectedState, setSelectedState] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Color scheme
  const COLORS = {
    scheme: '#A7E1E9',       // Light blue for schemes
    document: '#283E5E',    // Dark blue for documents
    schemeText: '#283E5E',  // Dark text for scheme labels
    documentText: '#283E5E' // White text for document labels
  };

  // Custom label component
  const renderCustomizedLabel = ({
  cx,
  cy,
  midAngle,
  innerRadius,
  outerRadius,
  percent,
  index,
  name
}) => {
  const RADIAN = Math.PI / 180;
  // Calculate position for outer label
  const radius = outerRadius * 1.2; // Position outside the pie
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  return (
    <text
      x={x}
      y={y}
      fill={index === 0 ? '#283E5E' : '#283E5E'} // Dark for scheme, white for document
      textAnchor="middle"
      dominantBaseline="central"
      className="text-xs font-medium"
    >
      {`${name} ${(percent * 100).toFixed(0)}%`}
    </text>
  );
};

  // Fetch states list
  useEffect(() => {
    const fetchStates = async () => {
      try {
        const response = await fetch(
          `https://api-project360-insights.haqdarshak.com/kpi/scheme-diversity/unique-state/?pid=${pid}`
        );
        if (!response.ok) throw new Error('Failed to fetch states');
        const data = await response.json();
        setStates(data.states || []);
      } catch (err) {
        console.error('Error fetching states:', err);
      }
    };

    if (pid) fetchStates();
  }, [pid]);

  // Fetch scheme-document data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        let url = `https://api-project360-insights.haqdarshak.com/kpi/scheme-diversity/scheme-document/br/?pid=${pid}`;
        if (selectedState) {
          url += `&state=${selectedState}`;
        }

        const response = await fetch(url);
        if (!response.ok) throw new Error('Failed to fetch data');
        const jsonData = await response.json();
        setData(jsonData);
      } catch (err) {
        setError(err.message);
        console.error('Error fetching data:', err);
      } finally {
        setLoading(false);
      }
    };

    if (pid) fetchData();
  }, [pid, selectedState]);

  const { scheme_count = 0, document_count = 0 } = data;
  const total = scheme_count + document_count;
  const schemePercentage = total > 0 ? ((scheme_count / total) * 100).toFixed(1) : 0;
  const documentPercentage = total > 0 ? ((document_count / total) * 100).toFixed(1) : 0;

  const chartData = [
    { 
      name: 'Scheme', 
      value: scheme_count, 
      percentage: schemePercentage, 
      color: COLORS.scheme,
    },
    { 
      name: 'Document', 
      value: document_count, 
      percentage: documentPercentage, 
      color: COLORS.document,
    },
  ];

  return (
    <div className="bg-white rounded-xl ml-[2vw] max-w-[90vw] shadow-md mt-8 h-full">
      <div className="flex justify-between items-center mb-6">
        
        <div className="flex items-center space-x-4">
          <div className='ml-[20vw] text-lg font-semibold'>
            Filter by State
          {states.length > 0 && (
            <select
              value={selectedState}
              onChange={(e) => setSelectedState(e.target.value)}
              className="px-3 ml-5 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">All States</option>
              {states.map((state) => (
                <option key={state} value={state}>
                  {state}
                </option>
              ))}
            </select>
          )}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      ) : error ? (
        <div className="text-red-500 text-center py-8">{error}</div>
      ) : (
        <div className="flex flex-col md:flex-row gap-8">
          {/* Chart */}
          <div className="w-full md:w-2/3 h-96">
            <ResponsiveContainer width="100%" height="100%" >
              <PieChart margin={{ top: 50, right: 30, left: 20, bottom: 50 }}>
                <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                innerRadius="50%"
                outerRadius="90%" // Reduced to make space for labels
                paddingAngle={2}
               dataKey="value"
               label={renderCustomizedLabel} // Use custom label renderer
              labelLine={{ stroke: '#FFF', strokeWidth: 3 }} // Style for the connecting lines
              >
  {chartData.map((entry, index) => (
    <Cell key={`cell-${index}`} fill={entry.color} />
  ))}
</Pie>
                <Tooltip 
                  formatter={(value, name, props) => [
                    `${value.toLocaleString()} (${props.payload.percentage}%)`,
                    name
                  ]}
                  contentStyle={{
                    backgroundColor: '#fff',
                    borderColor: '#ddd',
                    borderRadius: '4px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}
                />
                {/* <Legend 
                  formatter={(value, entry, index) => (
                    <span className="text-gray-700">
                      {value}: {chartData[index].value.toLocaleString()} ({chartData[index].percentage}%)
                    </span>
                  )}
                /> */}
              </PieChart>
            </ResponsiveContainer>
          </div>

          {/* Stats */}
          <div className="w-full md:w-1/3 space-y-6">
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="text-sm font-medium text-gray-500">TOTAL BR Cases</h3>
              <p className="text-3xl font-bold text-gray-800 mt-1">
                {total.toLocaleString()}
              </p>
            </div>

            <div className="space-y-4">
              {chartData.map((item, index) => (
                <div key={index} className="space-y-1">
                  <div className="flex items-center">
                    <div
                      className="w-3 h-3 rounded-full mr-2" 
                      style={{ backgroundColor: item.color }}
                    ></div>
                    <span className="text-sm font-medium text-gray-700">{item.name}</span>
                  </div>
                  <p className="text-lg font-semibold text-gray-800 ml-5">
                    {item.value.toLocaleString()} <span className="text-gray-500">({item.percentage}%)</span>
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SchemeDocumentRatioDashboard;